import React, { FC, useCallback, useContext, useMemo } from 'react';
import classNames from 'classnames';
import ReorderResizeTable from 'components/ReorderResizeTable';
import { Contact } from 'models/Contact';
import {
  CUSTOM_COL_SIZES,
  EnterpriseContactsPeopleTable,
  HEADING_TRANSLATIONS,
  SORTING_VALUES,
  TABLE_COLUMNS,
} from './components/ManagePeopleTableCells/data';
import { BODY_CONTENT_GENERATE } from './components/ManagePeopleTableCells';
import { Checkbox } from 'ncoded-component-library';
import ContactsContext from '../../../../providers/Contacts/Contacts.context';
import { RightClickDropdown } from 'components/RightClickDropdown';
import AddProfile from 'icons/AddProfile.icon';
import TrashIcon from 'icons/Trash.icon';
import { useTranslation } from 'react-i18next';
import { getHasElementsInArray } from 'utils';
import useContactsFilterOptions from '../ContactsFilterControls/useContactsFilterOptions';
import { ColumnSizing } from 'components/ReorderResizeTable/types';

type ContactsTableProps = {
  className?: string;
  isLoading?: boolean;
  contacts: Contact[];
  isDeleting: boolean;
  totalPages: number;
  totalItems?: number;
  columns: Array<keyof EnterpriseContactsPeopleTable>;
  columnSizing: ColumnSizing;
  handleEdit: (contact: Contact) => void;
  onReorder: (columns: Array<keyof EnterpriseContactsPeopleTable>) => void;
  onColumnSizingChange: (columnSizing: ColumnSizing) => void;
};

const ContactsTable: FC<ContactsTableProps> = ({
  className,
  contacts,
  isLoading,
  isDeleting,
  totalPages,
  totalItems,
  columns,
  columnSizing,
  handleEdit,
  onReorder,
  onColumnSizingChange,
}) => {
  const classes = classNames('contacts-table', className);

  const { t } = useTranslation();

  const {
    selected,
    isAllSelected,
    selectAll,
    removeAll,
    handleManage,
    handleDelete: handleRightClickDelete,
  } = useContext(ContactsContext);

  const hasSelected = getHasElementsInArray(selected);

  const { columnFilterOptions } = useContactsFilterOptions();

  const dropdownOptions = useCallback(
    (contacts: Contact[]) => {
      const values = hasSelected ? selected : contacts;
      return [
        {
          icon: <AddProfile />,
          label: t('addToADeal'),
          className: 'add-profile',
          onClick: () => handleManage(values),
        },
        {
          alert: true,
          label: t('delete'),
          icon: <TrashIcon />,
          onClick: () => handleRightClickDelete(values),
        },
      ];
    },
    [handleManage, handleRightClickDelete, hasSelected, selected, t],
  );

  const renderedContent = useMemo(
    () =>
      TABLE_COLUMNS.reduce(
        (acc, col) => ({
          ...acc,
          [col]: (contact: Contact) => (
            <RightClickDropdown
              options={dropdownOptions(hasSelected ? selected : [contact])}
            >
              {BODY_CONTENT_GENERATE[col]({
                contact,
                isDeleting,
                handleEdit,
              })}
            </RightClickDropdown>
          ),
        }),
        {},
      ),
    [dropdownOptions, handleEdit, hasSelected, isDeleting, selected],
  );

  const headerColTransformation = useMemo(
    () => ({
      checkbox: () => (
        <Checkbox
          checked={isAllSelected}
          onChange={() => (isAllSelected ? removeAll() : selectAll())}
        />
      ),
    }),
    [isAllSelected, removeAll, selectAll],
  );

  const checkIfContactIsSelected = useCallback(
    (contact) => !!selected.find(({ _id }) => _id === contact._id),
    [selected],
  );

  return (
    <ReorderResizeTable
      className={classes}
      columns={columns}
      headingTranslations={HEADING_TRANSLATIONS}
      sortingValues={SORTING_VALUES}
      columnFilterOptions={columnFilterOptions}
      data={contacts as unknown as EnterpriseContactsPeopleTable[]}
      isDataLoading={isLoading}
      tableContentTransformations={renderedContent}
      customSizes={CUSTOM_COL_SIZES}
      totalPages={totalPages}
      totalItems={totalItems}
      recordName="contact"
      getIsRowSelected={checkIfContactIsSelected}
      onRowClick={(contact) => handleEdit(contact as unknown as Contact)}
      tableHeaderContentTransformations={headerColTransformation}
      columnSizing={columnSizing}
      onColumnSizingChange={onColumnSizingChange}
      onReorder={onReorder}
    />
  );
};

export default ContactsTable;
